import { WithConfirmationDialog, withConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import {
    IPrAbsenceCategoryEntity,
    IPrAttendanceDayEntity,
    IPrAttendanceDayIntervalEntity,
    IPrAttendanceEntity,
    PrAttendanceDayEntity,
    PrAttendanceDayIntervalEntity,
    PrAttendanceEntity
} from "@odata/GeneratedEntityTypes";
import { AttendanceFastEntry, IntervalType } from "@pages/payroll/attendance/Attendance.types";
import { timeDiffPath } from "@pages/payroll/workingPatterns/WorkingPatternsDef";
import { isDefined, isNotDefined, isObjectEmpty } from "@utils/general";
import { cloneDeep } from "lodash";
import React, { Component } from "react";

import CollapsibleSection from "../../../components/collapsibleSection/CollapsibleSection";
import FieldsWrapper from "../../../components/inputs/field/FieldsWrapper";
import SmartFastEntryList, {
    ActionType,
    addEmptyLineItem,
    ISmartFastEntriesActionEvent
} from "../../../components/smart/smartFastEntryList";
import SmartField, { ISmartFieldBlur, ISmartFieldChange } from "../../../components/smart/smartField/SmartField";
import { ModelEvent } from "../../../model/Model";
import BindingContext from "../../../odata/BindingContext";
import TestIds from "../../../testIds";
import DateType, { getUtcDayjs } from "../../../types/Date";
import { FormStorage } from "../../../views/formView/FormStorage";
import {
    DayWorkStartPath,
    entriesBackupPath,
    HasAbsencePath,
    IsFullDayAbsencePath,
    PlaceholderEventLinkPath,
    SaldoPath,
    SocialEventLinkPath,
    WorkedHoursPath
} from "./Attendance.def";
import { DayEditWrapper } from "./Attendance.styles";
import {
    ATTENDANCE_FAKE_ID,
    createNewAttendanceDayEntity,
    generateIntervalsForDay,
    getAbsenceIntervals,
    getAllAbsenceTime,
    getDayByIndex,
    getDayIntervalsMinutes,
    getFilteredAbsencesForDay,
    getPlaceholders,
    getSortedIntervals,
    getTopPriorityDayAbsence,
    getWorkingPattern,
    getWorkIntervals,
    hasWholeDaySocialEvent,
    isAbsenceDisabled,
    isValidInterval,
    parseDayActionsFromData,
    refreshActions,
    refreshBalance,
    refreshSaldoMap,
    refreshWorkedHoursMap,
    transformDateToTodayTime
} from "./Attendance.utils";
import { IAttendanceCustomData } from "./AttendanceFormView";

interface IProps extends WithConfirmationDialog {
    storage: FormStorage<IPrAttendanceEntity, IAttendanceCustomData>;
    selectedDay: number;
    onChange: (args: ISmartFieldChange) => void;
}

const workColumns = [
    { id: PrAttendanceDayIntervalEntity.WorkType },
    { id: PrAttendanceDayIntervalEntity.TimeStart },
    { id: PrAttendanceDayIntervalEntity.TimeEnd },
    { id: timeDiffPath }
];

const absenceColumns = [
    { id: PrAttendanceDayIntervalEntity.AbsenceCategory },
    { id: PrAttendanceDayIntervalEntity.TimeStart },
    { id: PrAttendanceDayIntervalEntity.TimeEnd }
];

class AttendanceDayEditEntries extends Component<IProps> {
    componentDidMount() {
        this.init();
    }

    componentDidUpdate(prevProps: Readonly<IProps>) {
        if (this.props.selectedDay !== prevProps.selectedDay) {
            this.init();
        }
    }

    get selectedDayBc(): BindingContext {
        const { storage, selectedDay } = this.props;
        let day = storage.data.entity.Days?.find(day => getUtcDayjs(day.Date).get("date") === selectedDay);
        if (!day) {
            day = createNewAttendanceDayEntity(selectedDay, storage);
        }
        return storage.data.bindingContext.navigate(PrAttendanceEntity.Days).addKey(day);
    }

    get selectedDay(): IPrAttendanceDayEntity {
        return this.selectedDayBc ? this.props.storage.getValue(this.selectedDayBc) : null;
    }

    get minutesFund(): number {
        return this.selectedDay.Planned ?? 0;
    }

    get hasAbsence(): boolean {
        return !!getAbsenceIntervals(this.selectedDay)?.length;
    }

    getDayBc = (day: IPrAttendanceDayEntity) => {
        return this.props.storage.data.bindingContext.navigate(PrAttendanceEntity.Days).addKey(day);
    }

    init = () => {
        const storage = this.props.storage;
        const socialEventTopPriorityAbsence = getTopPriorityDayAbsence(this.selectedDay);
        const isWholeDay = isDefined(socialEventTopPriorityAbsence) ? socialEventTopPriorityAbsence.IsWholeDay : getAbsenceIntervals(this.selectedDay).some(i => i.IsWholeDay);
        const sortedIntervals = getSortedIntervals(this.selectedDay);
        const workingPattern = getWorkingPattern(storage.data.entity);

        storage.setValue(this.selectedDayBc.navigate(HasAbsencePath), this.hasAbsence);
        storage.setValue(this.selectedDayBc.navigate(IsFullDayAbsencePath), isWholeDay);
        storage.setValue(this.selectedDayBc.navigate(DayWorkStartPath), sortedIntervals[0]?.TimeStart ?? workingPattern.DefaultDayStart);

        this.recalculateWorkingHours();
        storage.refresh();
    };

    recalculateWorkingHours = (overwrite?: boolean) => {
        const storage = this.props.storage;

        for (const day of this.getSelectedDays()) {
            const workingMinutes = getDayIntervalsMinutes(day, IntervalType.Work);
            const workingHoursBc = this.getDayBc(day).navigate(WorkedHoursPath);

            if (workingMinutes > 0) {
                day.IsExcludedDay = false;
                day.IsExcludedPeriod = false;
            }

            if (overwrite || !storage.getValue(workingHoursBc)) {
                const time = getUtcDayjs().startOf("day").add(workingMinutes, "minutes").toDate();
                storage.setValue(workingHoursBc, time);
                storage.addActiveField(workingHoursBc);
            }
        }
    };

    refreshCalculatedFields = (): void => {
        const storage = this.props.storage;
        refreshSaldoMap(storage);
        refreshWorkedHoursMap(storage);
        refreshBalance(storage);
        refreshActions(storage);
        this.recalculateWorkingHours(true);
        storage.refresh();
    };

    handleAbsenceCategoryChange = (e: ISmartFieldChange): void => {
        if (e.triggerAdditionalTasks && e.bindingContext.getPath() === PrAttendanceDayIntervalEntity.AbsenceCategory) {
            const { storage } = this.props;
            const absenceCategory = e.additionalData as IPrAbsenceCategoryEntity;

            const actions = parseDayActionsFromData(storage.data.entity.Days);
            storage.setCustomData({ actions });

            const hasOneAbsence = getAbsenceIntervals(this.selectedDay).length === 1;

            const getDefaultValue = (path: PrAttendanceDayEntity.IsExcludedDay | PrAttendanceDayEntity.IsExcludedPeriod | PrAttendanceDayEntity.IsOnRegistry): boolean => {
                const categoryValue = !!absenceCategory[path];
                if (hasOneAbsence) {
                    return categoryValue;
                }
                return !!this.selectedDay[path] || categoryValue;
            };

            this.getSelectedDays().forEach(day => {
                day.IsExcludedDay = getDefaultValue(PrAttendanceDayEntity.IsExcludedDay);
                day.IsExcludedPeriod = getDefaultValue(PrAttendanceDayEntity.IsExcludedPeriod);
                day.IsOnRegistry = getDefaultValue(PrAttendanceDayEntity.IsOnRegistry);
            });

            this.copyIntervalsToOtherSelected();
            this.refreshCalculatedFields();
        }
    }

    handleAbsenceStartChange = (e: ISmartFieldChange) => {
        const storage = this.props.storage;
        if (e.bindingContext.getPath() === PrAttendanceDayIntervalEntity.TimeStart && storage.getValue(e.bindingContext.getParent())?.AbsenceCategory?.Id) {
            const value = getUtcDayjs(transformDateToTodayTime(e.value as Date));
            const workStartBc = e.bindingContext.getParent().getParent().navigate(DayWorkStartPath);
            const defaultStart = getUtcDayjs(transformDateToTodayTime(storage.getValue(workStartBc)));
            const interval = storage.getValue(e.bindingContext.getParent());
            if (value.isBefore(defaultStart)) {
                interval.IsStartNextDay = true;
                interval.IsEndNextDay = true;
            } else if (value.isSameOrAfter(defaultStart)) {
                interval.IsStartNextDay = false;
                const timeEndIsLowerThanTimeStart = transformDateToTodayTime(interval.TimeEnd).diff(transformDateToTodayTime(interval.TimeStart), "minutes") < 0;
                interval.IsEndNextDay = timeEndIsLowerThanTimeStart ? true : interval.IsStartNextDay;
            }
        }
    };

    handleDayStartChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === DayWorkStartPath) {
            const value = e.value as Date;
            if (!DateType.isValid(value)) {
                return;
            }
            generateIntervalsForDay(this.selectedDayBc, this.minutesFund, this.props.storage);
            this.copyIntervalsToOtherSelected();
            this.refreshCalculatedFields();
        }
    };

    handleIntervalChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getParent().getPath(true) === PrAttendanceDayEntity.Intervals) {
            const storage = this.props.storage;
            const interval = storage.getValue(e.bindingContext.getParent());
            if (isValidInterval(interval)) {
                const timeEndIsLowerThanTimeStart = transformDateToTodayTime(interval.TimeEnd).diff(transformDateToTodayTime(interval.TimeStart), "minutes") < 0;
                interval.IsEndNextDay = timeEndIsLowerThanTimeStart ? true : interval.IsStartNextDay;
                this.copyIntervalsToOtherSelected();
                this.refreshCalculatedFields();

                if (isObjectEmpty(interval.AbsenceCategory) &&
                    (e.bindingContext.getPath() === PrAttendanceDayIntervalEntity.TimeStart && interval.Order === 1)) {
                    const workStartBc = e.bindingContext.getParent().getParent().navigate(DayWorkStartPath);
                    storage.setValue(workStartBc, interval.TimeStart);
                }
            }
        }
    };

    handleChange = (e: ISmartFieldChange): void => {
        const storage = this.props.storage;
        this.handleHasAbsenceChange(e);
        this.handleIsFullDayAbsence(e);
        this.handleWorkedHoursChange(e);
        this.props.onChange(e);
        this.handleIntervalChange(e);
        // must be called AFTER default change
        this.handleDayStartChange(e);
        this.handleAbsenceSettingsChange(e);
        this.handleFundChange(e);
        this.handleAbsenceCategoryChange(e);
        this.handleAbsenceStartChange(e);

        storage.refreshFields();
    };

    getSelectedDays = () => {
        const storage = this.props.storage;
        const selectedDays = storage.getCustomData().selectedDays;
        return Array.from(selectedDays?.keys() || []).map(day => {
            return getDayByIndex(storage.data.entity, day) ?? createNewAttendanceDayEntity(day, storage);
        });
    }

    // if user selected more days edit first, and copy intervals to other selected days
    copyIntervalsToOtherSelected = (): void => {
        this.getSelectedDays().forEach(day => {
            if (day === this.selectedDay) {
                return;
            }
            const intervals = cloneDeep(this.selectedDay.Intervals);
            if (!day.Planned) { // 0 or null as some days don't even exist yet as entity
                intervals.forEach((interval) => {
                    if (interval.IsWholeDay) {
                        // DEV-44843
                        interval.TimeEnd = interval.TimeStart;
                    }
                });
            }
            day.Intervals = intervals;
        });
    };

    handleAbsenceSettingsChange = (e: ISmartFieldChange): void => {
        const path = e.bindingContext.getPath() as PrAttendanceDayEntity;
        if (path === PrAttendanceDayEntity.IsExcludedDay || path === PrAttendanceDayEntity.IsExcludedPeriod || path === PrAttendanceDayEntity.IsOnRegistry) {
            this.getSelectedDays().forEach(day => {
                day[path] = !!e.value;
            });
            this.refreshCalculatedFields();
        }
    };

    handleFundChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === PrAttendanceDayEntity.Planned) {
            const value = Number(e.value);
            if (isNotDefined(e.value) || isNaN(value)) {
                return;
            }
            this.getSelectedDays().forEach(day => {
                day.Planned = value;
                getAbsenceIntervals(day, true).forEach(interval => {
                    if (interval.IsWholeDay) {
                        if (!interval.TimeStart) {
                            const defaultStart = getUtcDayjs(this.props.storage.getValue(this.selectedDayBc.navigate(DayWorkStartPath)));
                            interval.TimeStart = getUtcDayjs().startOf("day")
                                .add(defaultStart.get("hours"), "hours")
                                .add(defaultStart.get("minutes"), "minutes")
                                .toDate();
                        }
                        interval.TimeEnd = getUtcDayjs(interval.TimeStart).add(value, "minutes").toDate();
                    }
                });
            });

            this.refreshCalculatedFields();
        }
    };

    handleWorkedHoursChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === WorkedHoursPath) {
            const value = e.value as Date;
            if (!DateType.isValid(value)) {
                return;
            }
            const minutes = getUtcDayjs(value).get("hour") * 60 + getUtcDayjs(value).get("minute");
            this.getSelectedDays().forEach(day => {
                generateIntervalsForDay(this.getDayBc(day), minutes, this.props.storage, true);
            });
            this.refreshCalculatedFields();
        }
    };

    handleHasAbsenceChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === HasAbsencePath) {
            const storage = this.props.storage;

            for (const day of this.getSelectedDays()) {
                const bc = this.getDayBc(day);
                // TODO: neni vyresena indikace pokud uzivatel zada absenci ale nevybere typ
                // nejde pak ulozit -> bud odstranit v before save nebo dat nejakej mark
                if (e.value) {
                    storage.setValue(bc.navigate(IsFullDayAbsencePath), true);
                    storage.setValue(bc.navigate(entriesBackupPath), day.Intervals.filter(i => !i.IsWholeDay || i.IsPlaceholder) ?? []);
                } else {
                    const placeholders = getPlaceholders(day);
                    storage.setValue(bc.navigate(IsFullDayAbsencePath), false);
                    storage.setValue(bc.navigate(PrAttendanceDayEntity.IsExcludedDay), false);
                    storage.setValue(bc.navigate(PrAttendanceDayEntity.IsOnRegistry), true);
                    storage.setValue(bc.navigate(PrAttendanceDayEntity.IsExcludedPeriod), false);
                    const backup = storage.getValue(bc.navigate(entriesBackupPath)) as IPrAttendanceDayIntervalEntity[];
                    if (backup?.length) {
                        storage.setValue(bc.navigate(PrAttendanceDayEntity.Intervals), backup);
                    } else {
                        generateIntervalsForDay(bc, day.Planned, storage);
                        storage.setValue(bc.navigate(PrAttendanceDayEntity.Intervals), [...getWorkIntervals(day), ...placeholders]);
                    }
                }
            }

            if (e.value) {
                this.addNewWholeDayAbsence(undefined);
                this.copyIntervalsToOtherSelected();
            }
            setTimeout(() => {
                storage.emitter.emit(ModelEvent.RecalculateFastEntryList);
                storage.refresh();
            }, 1000);
            this.refreshCalculatedFields();
        }
    };


    addNewWholeDayAbsence = (absence?: IPrAbsenceCategoryEntity): void => {
        const storage = this.props.storage;
        const placeholders = getPlaceholders(this.selectedDay);
        const intervalsBc = this.selectedDayBc.navigate(PrAttendanceDayEntity.Intervals);

        const newItem = addEmptyLineItem({
            storage,
            bindingContext: intervalsBc,
            newItemsCount: 1,
            index: 1,
            columns: [],
            context: storage.context,
            order: PrAttendanceDayIntervalEntity.Order,
            defaultData: {
                IsWholeDay: true,
                AbsenceCategory: {
                    Id: absence?.Id ?? ATTENDANCE_FAKE_ID,
                    Color: absence?.Color,
                    CurrentTemporalPropertyBag: absence ? {
                        Name: absence.CurrentTemporalPropertyBag?.Name
                    } : null
                }
            }
        });

        const defaultStart = getUtcDayjs(storage.getValue(this.selectedDayBc.navigate(DayWorkStartPath)));
        const startDayJs = getUtcDayjs().startOf("day")
            .add(defaultStart.get("hours"), "hours")
            .add(defaultStart.get("minutes"), "minutes");
        newItem.TimeStart = startDayJs.toDate();
        newItem.TimeEnd = startDayJs.add(this.minutesFund, "minutes").toDate();

        storage.setValue(intervalsBc, [newItem, ...placeholders]);
    };

    handleIsFullDayAbsence = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === IsFullDayAbsencePath) {
            if (e.value === true) {
                this.addNewWholeDayAbsence(this.selectedDay.Intervals?.find(int => !!int.AbsenceCategory)?.AbsenceCategory);
            } else {
                for (const interval of (this.selectedDay.Intervals.filter(i => !i.IsPlaceholder) ?? [])) {
                    interval.IsWholeDay = false;
                }
                const remainingMinutes = Math.max(0, this.minutesFund - getAllAbsenceTime(getAbsenceIntervals(this.selectedDay)));
                generateIntervalsForDay(this.selectedDayBc, remainingMinutes, this.props.storage);
            }
            this.props.storage.emitter.emit(ModelEvent.RecalculateFastEntryList);
            this.copyIntervalsToOtherSelected();
            this.refreshCalculatedFields();
        }
    };

    handleAction = (e: ISmartFastEntriesActionEvent<IPrAttendanceDayIntervalEntity>) => {
        const storage = this.props.storage;
        const intervals = this.selectedDay.Intervals ?? [];

        if (e.groupId === AttendanceFastEntry.Absence) {
            const [item] = e.affectedItems;
            if (e.actionType === ActionType.Add) {
                item.AbsenceCategory = {
                    Id: ATTENDANCE_FAKE_ID
                };
                item.Order = intervals.length + 1;
                e.items = [...intervals, item];
            }
        }

        if (e.groupId === AttendanceFastEntry.Work && e.actionType === ActionType.Add) {
            const item = e.affectedItems[0];
            item.TimeStart = intervals[intervals.length - 1]?.TimeEnd;
            item.Order = intervals.length + 1;
            e.items = [...intervals, item];
        }

        storage.handleLineItemsAction(e);

        if (e.actionType === ActionType.Remove) {
            this.recalculateWorkingHours(true);
            refreshWorkedHoursMap(storage);
            refreshSaldoMap(storage);
            refreshBalance(storage);
            refreshActions(storage);
        }

        if (e.actionType === ActionType.Reorder) {
            if (e.groupId === AttendanceFastEntry.Work) {
                const firstItemTimeStart = e.items.find(i => i.Order === 1)?.TimeStart;
                if (DateType.isValid(firstItemTimeStart)) {
                    storage.setValue(this.selectedDayBc.navigate(DayWorkStartPath), firstItemTimeStart);
                }
            }
            refreshActions(storage);
        }

        storage.refresh();
    };

    handleIntervalBlur = (e: ISmartFieldBlur): void => {
        if (e.bindingContext.getParent().getPath(true) === PrAttendanceDayEntity.Intervals) {
            const storage = this.props.storage;
            const interval = storage.getValue(e.bindingContext.getParent());
            if (isValidInterval(interval) && !isObjectEmpty(interval.AbsenceCategory) && !interval.IsWholeDay && e.wasChanged) {
                const remainingMinutes = Math.max(0, this.minutesFund - getAllAbsenceTime(getAbsenceIntervals(this.selectedDay)));
                generateIntervalsForDay(this.selectedDayBc, remainingMinutes, storage);
                this.copyIntervalsToOtherSelected();
                this.refreshCalculatedFields();
            }
        }
    };

    handleBlur = async (e: ISmartFieldBlur): Promise<void> => {
        this.handleIntervalBlur(e);
        await this.props.storage.handleBlur(e);
        this.props.storage.refreshFields();
    };

    renderFieldForPath = (path: string) => {
        return <SmartField storage={this.props.storage}
                           bindingContext={this.selectedDayBc.navigate(path)}
                           onBlur={this.handleBlur}
                           onChange={this.handleChange}/>;
    };

    render() {
        if (!this.selectedDay) {
            return null;
        }
        const storage = this.props.storage;
        const hasAbsence = storage.getValue(this.selectedDayBc.navigate(HasAbsencePath));
        const isFullDayAbsence = hasAbsence && storage.getValue(this.selectedDayBc.navigate(IsFullDayAbsencePath));
        const columnsLength = isFullDayAbsence ? 1 : 3;

        const commonFastEntryProps = {
            storage,
            bindingContext: this.selectedDayBc.navigate(PrAttendanceDayEntity.Intervals),
            onChange: this.handleChange,
            onBlur: this.handleBlur,
            useLabelWrapping: true,
            isReadOnly: storage.isReadOnly,
            renderAsFastEntryForReadOnly: true,
            onAction: this.handleAction,
            isItemCloneable: false,
            isItemDisabled: isAbsenceDisabled,
            isItemRemovable: !isFullDayAbsence,
            disableVirtualization: true,
            order: PrAttendanceDayIntervalEntity.Order
        };

        const hasWholeDaySocialEventWithTopPriority = hasWholeDaySocialEvent(this.selectedDay) && !!getTopPriorityDayAbsence(this.selectedDay)?.IsWholeDay;

        return <DayEditWrapper data-testid={TestIds.CalendarDayDetail}>
            <FieldsWrapper>
                {this.renderFieldForPath(HasAbsencePath)}
                {this.renderFieldForPath(IsFullDayAbsencePath)}
            </FieldsWrapper>
            {hasAbsence && !hasWholeDaySocialEventWithTopPriority && <SmartFastEntryList
                {...commonFastEntryProps}
                groupId={AttendanceFastEntry.Absence}
                filterItems={(items) => {
                    const res = (items as IPrAttendanceDayIntervalEntity[]).filter(i => {
                        return !!i.AbsenceCategory?.Id && !i.IsPlaceholder;
                    });

                    // this is case when there is whole day social event and other social events which is not whole day and has higher priority
                    // we want to show only non whole day absences in this case
                    const topPrioritySocialEventAbsence = getTopPriorityDayAbsence(this.selectedDay);
                    if (!isObjectEmpty(topPrioritySocialEventAbsence) && !topPrioritySocialEventAbsence.IsWholeDay) {
                        return res.filter(i => !i.IsWholeDay);
                    }

                    return res;
                }}
                columns={absenceColumns.slice(0, columnsLength)}
                isReadOnly={storage.isReadOnly}
                canAdd={!isFullDayAbsence}
            />}
            <FieldsWrapper>
                {this.renderFieldForPath(SocialEventLinkPath)}
                {this.renderFieldForPath(PlaceholderEventLinkPath)}
            </FieldsWrapper>
            <FieldsWrapper>
                {this.renderFieldForPath(PrAttendanceDayEntity.Planned)}
                {this.renderFieldForPath(WorkedHoursPath)}
                {this.renderFieldForPath(SaldoPath)}
            </FieldsWrapper>
            <FieldsWrapper style={{ marginTop: "17px" }}>
                {this.renderFieldForPath(DayWorkStartPath)}
            </FieldsWrapper>
            <FieldsWrapper>
                <CollapsibleSection
                    title={this.props.storage.t("Attendance:Form.RecordedWorkingHours")}
                    defaultIsOpen={false}
                    usedAsField
                    removeBodyPadding
                >
                    <SmartFastEntryList
                        {...commonFastEntryProps}
                        groupId={AttendanceFastEntry.Work}
                        filterItems={(items) => {
                            return getFilteredAbsencesForDay(items as IPrAttendanceDayIntervalEntity[], this.selectedDay);
                        }}
                        isReadOnly={storage.isReadOnly || isFullDayAbsence}
                        columns={workColumns}
                        canAdd={true}
                        canReorder={true}
                        isAddDisabled={isFullDayAbsence}
                    />
                </CollapsibleSection>
            </FieldsWrapper>
            {this.hasAbsence && <FieldsWrapper>
                <CollapsibleSection
                    title={this.props.storage.t("Attendance:Form.OtherSettings")}
                    defaultIsOpen={false}
                    usedAsField
                    removeBodyPadding
                >
                    {this.renderFieldForPath(PrAttendanceDayEntity.IsExcludedPeriod)}
                    {this.renderFieldForPath(PrAttendanceDayEntity.IsExcludedDay)}
                    {this.renderFieldForPath(PrAttendanceDayEntity.IsOnRegistry)}
                </CollapsibleSection>
            </FieldsWrapper>}
        </DayEditWrapper>;
    }
}

export default withConfirmationDialog(AttendanceDayEditEntries);